import { z } from "zod";

import {
  EMAIL_MESSAGE,
  MAX_LENGTH_MESSAGE,
  MIN_LENGTH_MESSAGE,
} from "@/utils";

const stringPreprocess = (val: unknown): string => {
  if (val === undefined || val === null) {
    return "";
  }
  return String(val);
};

export const organizationRegisterFormSchema = z
  .object({
    document_type: z.enum(["cnpj", "cpf"]),
    name: z.preprocess(
      stringPreprocess,
      z
        .string()
        .min(2, { message: MIN_LENGTH_MESSAGE("2") })
        .max(150, { message: MAX_LENGTH_MESSAGE("150") })
    ),
    cnpj: z.preprocess(stringPreprocess, z.string()).optional(),
    cpf: z.preprocess(stringPreprocess, z.string()).optional(),
    corporate_name: z.preprocess(
      stringPreprocess,
      z
        .string()
        .max(150, { message: MAX_LENGTH_MESSAGE("150") })
    ).optional(),
    email: z.preprocess(
      stringPreprocess,
      z.string().email({ message: EMAIL_MESSAGE })
    ),
    business_classification: z.preprocess(
      stringPreprocess,
      z.string().min(1, {
        message: "Selecione uma classificação de negócio",
      })
    ),
    address: z.object({
      postal_code: z.preprocess(
        stringPreprocess,
        z.string().min(8, { message: MIN_LENGTH_MESSAGE("8") })
      ),
      street: z.preprocess(
        stringPreprocess,
        z.string().min(2, { message: MIN_LENGTH_MESSAGE("2") })
      ),
      number: z.preprocess(
        stringPreprocess,
        z.string().min(1, { message: MIN_LENGTH_MESSAGE("1") })
      ),
      complement: z.preprocess(stringPreprocess, z.string()).optional(),
      neighborhood: z.preprocess(
        stringPreprocess,
        z.string().min(2, { message: MIN_LENGTH_MESSAGE("2") })
      ),
      city: z.preprocess(
        stringPreprocess,
        z.string().min(2, { message: MIN_LENGTH_MESSAGE("2") })
      ),
      state: z.preprocess(
        stringPreprocess,
        z.string().min(2, { message: MIN_LENGTH_MESSAGE("2") })
      ),
    }),
    company_representative: z.object({
      first_name: z.preprocess(
        stringPreprocess,
        z.string().min(2, { message: MIN_LENGTH_MESSAGE("2") })
      ),
      last_name: z.preprocess(
        stringPreprocess,
        z.string().min(2, { message: MIN_LENGTH_MESSAGE("2") })
      ),
      personal_email: z.preprocess(
        stringPreprocess,
        z.string().email({ message: EMAIL_MESSAGE })
      ),
      cpf: z.preprocess(
        stringPreprocess,
        z.string().min(11, { message: MIN_LENGTH_MESSAGE("11") })
      ),
      mobile_phone: z.preprocess(
        stringPreprocess,
        z.string().min(11, { message: MIN_LENGTH_MESSAGE("11") })
      ),
    }),
    bank_account: z.object({
      bank_name: z.preprocess(
        stringPreprocess,
        z.string().min(2, { message: MIN_LENGTH_MESSAGE("2") })
      ),
      bank_branch: z.preprocess(
        stringPreprocess,
        z.string().min(2, { message: MIN_LENGTH_MESSAGE("2") })
      ),
      checking_account: z.preprocess(
        stringPreprocess,
        z.string().min(2, { message: MIN_LENGTH_MESSAGE("2") })
      ),
      checking_account_digit: z.preprocess(
        stringPreprocess,
        z.string().min(1, { message: MIN_LENGTH_MESSAGE("1") })
      ),
    }),
    document: z.object({
      company_logo: z.instanceof(File).optional(),
      cnpj_card: z.instanceof(File).optional(),
      partners_documents_cpf: z.array(z.instanceof(File)).optional(),
      articles_of_incorporation: z.array(z.instanceof(File)).optional(),
      contract_signed: z.array(z.instanceof(File)).optional(),
    }),
    signed_terms: z.object({
      signed: z.boolean(),
      signed_at: z.string(),
      ip_address: z.string(),
    }),
  })
  .superRefine((data, ctx) => {
    if (data.document_type === "cnpj") {
      const clean = (data.cnpj ?? "").replace(/\D/g, "");
      if (clean.length < 14) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "CNPJ inválido (mínimo 14 dígitos)",
          path: ["cnpj"],
        });
      }
    } else {
      const clean = (data.cpf ?? "").replace(/\D/g, "");
      if (clean.length < 11) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "CPF inválido (mínimo 11 dígitos)",
          path: ["cpf"],
        });
      }
    }
  });

export type TOrganizationRegisterFormData = z.infer<typeof organizationRegisterFormSchema>;
